import { ChangeDetectorRef, Component, EventEmitter, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DialogButton } from '@snapon/design-system-angular';
import { AppInfoService } from 'core/app-info/app-info.service';
import { TouchpointsService } from '../services/touchpoints.service';
import { OutlookTouchpoint } from '../entities/outlook-touchpoint';
import { TouchpointsAccount } from '../entities/touchpoints-account';

@Component({
  selector: 'app-merge-touchpoint-modal',
  templateUrl: './merge-touchpoint-modal.component.html',
  styleUrls: ['./merge-touchpoint-modal.component.scss'],
  standalone: false
})
export class MergeTouchpointModalComponent implements OnInit {
  appStrings = this.appInfoService.appStrings;
  form: UntypedFormGroup;
  accountOptions: { value: string, label: string, description: string }[] = [];
  selectedAccount: TouchpointsAccount = null;
  loading = false;
  confirmDialogOpen$ = new EventEmitter<boolean>();
  confirmDialogContent = '';
  confirmDialogButtons: DialogButton[] = [];

  private allAccounts: TouchpointsAccount[] = [];

  get touchpoint(): OutlookTouchpoint {
    return this.data.touchpoint;
  }

  get touchpointTypeLabel(): string {
    return this.touchpointsService.templateValues?.TouchpointType?.[this.touchpoint.touchpointType] ?? '';
  }

  get lnStatuses(): { [key: number]: string } {
    return this.data.lnStatuses;
  }

  constructor(
    public dialogRef: MatDialogRef<MergeTouchpointModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      touchpoint: OutlookTouchpoint;
      amEmployeeId: string;
      lnStatuses: { [key: number]: string };
    },
    private fb: UntypedFormBuilder,
    private appInfoService: AppInfoService,
    private touchpointsService: TouchpointsService,
    private router: Router,
    private ref: ChangeDetectorRef,
  ) {
    this.form = this.fb.group({
      accountName: ['', Validators.required]
    });
  }

  ngOnInit() {
    this.form.controls.accountName.valueChanges.subscribe(accountId => {
      this.selectedAccount = this.allAccounts.find(a => a.id === accountId) ?? null;
    });

    this.loadAccounts();
  }

  private loadAccounts() {
    this.loading = true;
    this.touchpointsService.getAccounts(this.data.amEmployeeId, { name: '', address: '', status: undefined })
      .subscribe((accounts: TouchpointsAccount[]) => {
        this.allAccounts = accounts;
        this.accountOptions = accounts.map(a => ({
          value: a.id,
          label: this.formatAccountName(a),
          description: [a.bpNumber ? `BP#: ${a.bpNumber}` : null, a.address].filter(Boolean).join(', ')
        }));
        this.loading = false;
      });
  }

  private formatAccountName(account: TouchpointsAccount): string {
    return account.name1 + (account.name2 ? ` ${account.name2}` : '');
  }

  merge() {
    if (!this.selectedAccount) {
      return;
    }
    const accountName = this.formatAccountName(this.selectedAccount);
    this.confirmDialogContent = this.appStrings.touchpoints_mergeTouchpointConfirmContent
      .replace('{0}', this.touchpoint.subject ?? '')
      .replace('{1}', accountName);
    this.confirmDialogButtons = [
      { type: 'secondary', label: this.appStrings.button_cancel, onclick: () => { } },
      {
        type: 'primary', label: this.appStrings.touchpoints_mergeTouchpoint, onclick: () => {
          this.executeMerge();
        }
      }
    ];
    this.ref.detectChanges();
    this.confirmDialogOpen$.emit(true);
  }

  private executeMerge() {
    const route = this.selectedAccount.isLead ? '/accounts-leads' : '/accounts';
    this.router.navigate([route, this.selectedAccount.id, {
      tab: 'touchpoints',
      entityId: this.touchpoint.id,
      isMergingToCrm: true
    }]);
    this.dialogRef.close();
  }
}
